<template>
  <Teleport to="body">
    <transition name="imp-fade">
      <div
        v-if="visible && isMobile"
        class="imp-backdrop"
        aria-hidden="true"
        @click="onBackdropClick"
      />
    </transition>
    <transition name="imp-slide-fade">
      <div
        v-if="visible"
        ref="panelRef"
        class="imp-panel"
        :class="{ 'imp-panel--mobile': isMobile }"
        :style="panelStyle"
        role="dialog"
        aria-label="互动消息"
        @click.stop
        @mouseenter="onPanelMouseEnter"
        @mouseleave="onPanelMouseLeave"
      >
        <div class="imp-panel-inner">
          <div class="imp-head">
            <span class="imp-title">互动消息</span>
            <div class="imp-head-actions">
              <button
                v-if="token"
                type="button"
                class="imp-mark-all"
                aria-label="全部已读"
                title="全部已读"
                @click.stop="handleMarkAllReadClick"
              >
                <AppIcon name="markAllRead" size="sm" aria-label="全部已读" />
              </button>
              <button
                type="button"
                class="imp-close"
                aria-label="关闭"
                @click="close"
              >
                <AppIcon name="close" size="sm" aria-label="关闭" />
              </button>
            </div>
          </div>

          <div v-if="!token" class="imp-tip">请先登录后查看互动消息</div>

          <template v-else>
            <div v-if="loading && displayList.length === 0" class="imp-loading">
              加载中...
            </div>

            <div v-else-if="displayList.length > 0" class="imp-list-stack">
              <div
                ref="impScroll"
                class="imp-scroll"
                @scroll.passive="onImpScrollList"
              >
                <div class="im-list">
                  <div
                    v-for="item in displayList"
                    :key="item.interactionMessageId"
                    :class="['im-row', { 'im-row--unread': item.read === false }]"
                    @click="openComic(item)"
                  >
                    <span
                      v-if="item.read === false"
                      class="im-unread-dot"
                      aria-hidden="true"
                    />
                    <div class="im-avatar-wrap">
                      <img
                        :src="item.fromUserAvatar"
                        alt=""
                        class="im-avatar"
                        loading="lazy"
                      />
                      <span
                        v-if="item.interactionType === 'REPLY'"
                        class="im-type-badge im-type-badge--reply"
                        title="回复"
                      >
                        <AppIcon name="comment" size="xs" aria-label="回复" />
                      </span>
                      <span
                        v-else-if="item.interactionType === 'LIKE'"
                        class="im-type-badge im-type-badge--like"
                        title="点赞"
                      >
                        <AppIcon name="heart" size="xs" variant="active" class="im-like-icon" aria-label="点赞" />
                      </span>
                      <span
                        v-else-if="item.interactionType === 'FEEDBACK_REPLY'"
                        class="im-type-badge im-type-badge--feedback"
                        title="反馈回复"
                      >
                        <AppIcon name="feedback" size="xs" aria-label="反馈回复" />
                      </span>
                    </div>
                    <div class="im-main">
                      <p class="im-name">{{ item.fromUserName }}</p>
                      <p class="im-line">{{ formatInteractionLine(item) }}</p>
                      <p class="im-time">{{ item.interactionTime }}</p>
                    </div>
                    <img
                      v-if="item.comicCover"
                      :src="item.comicCover"
                      alt=""
                      class="im-thumb"
                      loading="lazy"
                    />
                  </div>
                </div>

                <div ref="listSentinel" class="im-sentinel" aria-hidden="true" />
                <p v-if="loadingMore" class="im-loading-hint">加载中...</p>
              </div>
              <div
                v-if="showInfiniteEndTip"
                class="infinite-list-page__spacer"
                aria-hidden="true"
              />
              <p v-if="showInfiniteEndTip" class="infinite-end-tip">
                <span class="infinite-end-tip-text">我也是有底线的</span>
              </p>
            </div>

            <div v-else class="imp-empty-wrap">
              <el-empty description="暂无互动消息" :image-size="72" />
            </div>
          </template>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { toRef } from 'vue'
import AppIcon from '@/shared/components/AppIcon.vue'
import { useInteractionMessagesPopover } from '@/features/message/composables/useInteractionMessagesPopover'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  getAnchor: {
    type: Function,
    default: null,
  },
})

const emit = defineEmits([
  'update:visible',
  'close',
  'panel-enter',
  'panel-leave',
  'unread-sync',
])

const {
  panelRef,
  impScroll,
  listSentinel,
  token,
  loading,
  loadingMore,
  displayList,
  showInfiniteEndTip,
  panelStyle,
  close,
  onBackdropClick,
  onPanelMouseEnter,
  onPanelMouseLeave,
  onImpScrollList,
  formatInteractionLine,
  handleMarkAllReadClick,
  openComic,
} = useInteractionMessagesPopover(
  toRef(props, 'visible'),
  toRef(props, 'isMobile'),
  () => (typeof props.getAnchor === 'function' ? props.getAnchor() : null),
  emit
)
</script>

<style scoped src="@/features/message/styles/InteractionMessagesPopover.css"></style>
